import { EventTimer } from '@guardian/commercial-core/event-timer';
import { isEligibleForTeads } from '@guardian/commercial-core/targeting/teads-eligibility';
import { breakpoints as sourceBreakpoints } from '@guardian/source/foundations';
import { once } from 'lodash-es';
import { getUrlVars } from '../lib/url';
import { initSlotIas } from './init-slot-ias';
const breakpointViewports = {
    mobile: [0, 0], // this is width 0 to cover all widths until the next breakpoint if any
    phablet: [sourceBreakpoints.phablet, 0],
    tablet: [sourceBreakpoints.tablet, 0],
    desktop: [sourceBreakpoints.desktop, 0],
    wide: [sourceBreakpoints.wide, 0],
};
const adUnit = once(() => {
    const urlVars = getUrlVars();
    return urlVars['ad-unit']
        ? `/${window.guardian.config.page.dfpAccountId}/${String(urlVars['ad-unit'])}`
        : window.guardian.config.page.adUnit;
});
const isBreakpoint = (breakpoint) => breakpoint in breakpointViewports;
const toGoogleTagSize = (size) => {
    // not using width and height here as to maintain compatibility with plain arrays
    return size[0] === 0 && size[1] === 0 ? 'fluid' : [size[0], size[1]];
};
/**
 *  Test a known good size mapping, if this fails we can't define slots!
 *  This can happen if googletag has been shimmed by an adblocker
 */
const canDefineSlot = once(() => {
    const testMapping = window.googletag
        .sizeMapping()
        .addSize([0, 0], [[300, 250]])
        .build();
    return !!testMapping;
});
/**
 * Builds a googletag size mapping based on the breakpoints and ad sizes from
 * the defined size mapping and the viewport sizes from source-foundations.
 */
const buildGoogletagSizeMapping = (sizeMapping) => {
    const mapping = window.googletag.sizeMapping();
    Object.entries(sizeMapping).forEach(([breakpoint, sizes]) => {
        if (isBreakpoint(breakpoint)) {
            mapping.addSize(breakpointViewports[breakpoint], sizes.map(toGoogleTagSize));
        }
    });
    return mapping.build();
};
const isMultiSize = (size) => {
    return Array.isArray(size) && !!size.length;
};
const isSizeInArray = (size, sizes) => {
    if (size === 'fluid') {
        return sizes.includes('fluid');
    }
    else if (Array.isArray(size)) {
        return !!sizes.find((item) => item[0] === size[0] && item[1] === size[1]);
    }
    return false;
};
/**
 * Take all the sizes in a size mapping and reduce to a single array of sizes, for use in `defineSlot`
 *
 * @todo this is possibly redundant as these are only used if a size mapping is not defined which we always provide.
 * @param sizeMapping googletag size mapping
 * @returns all the sizes that were present in the size mapping
 */
const collectSizes = (sizeMapping) => {
    const sizes = [];
    // as we're using sizeMapping, pull out all the ad sizes, as an array of arrays
    sizeMapping?.forEach(([, sizesForBreakpoint]) => {
        if (isMultiSize(sizesForBreakpoint)) {
            sizesForBreakpoint.forEach((size) => {
                if (!isSizeInArray(size, sizes)) {
                    sizes.push(size);
                }
            });
        }
    });
    return sizes;
};
const isEligibleForOutstream = (slotTarget) => typeof slotTarget === 'string' &&
    (slotTarget === 'inline1' || slotTarget === 'top-above-nav');
const allowSafeFrameToExpand = (slot) => {
    slot.setSafeFrameConfig({
        allowOverlayExpansion: false,
        allowPushExpansion: true,
        sandbox: true,
    });
    return slot;
};
class DefineSlotError extends Error {
    sizeMapping;
    report;
    constructor(message, sizeMapping, report = true) {
        super(message);
        this.name = 'DefineSlotError';
        this.sizeMapping = JSON.stringify(sizeMapping);
        this.report = report;
    }
}
const defineSlot = (adSlotNode, sizeMapping, slotTargeting = {}) => {
    const slotTarget = adSlotNode.getAttribute('data-name');
    EventTimer.get().mark('defineSlotStart', slotTarget);
    const id = adSlotNode.id;
    const googletagSizeMapping = buildGoogletagSizeMapping(sizeMapping);
    if (!googletagSizeMapping) {
        if (canDefineSlot()) {
            throw new DefineSlotError('googletag.sizeMapping did not return a size mapping', sizeMapping);
        }
        else {
            throw new DefineSlotError('Could not define slot. googletag.sizeMapping has been shimmed.', sizeMapping, false);
        }
    }
    const sizes = collectSizes(googletagSizeMapping);
    let slot;
    if (adSlotNode.getAttribute('data-out-of-page')) {
        slot = window.googletag.defineOutOfPageSlot(adUnit(), id);
        slot?.defineSizeMapping(googletagSizeMapping);
    }
    else {
        slot = window.googletag.defineSlot(adUnit(), sizes, id);
        slot?.defineSizeMapping(googletagSizeMapping);
        if (slot && isEligibleForOutstream(slotTarget)) {
            allowSafeFrameToExpand(slot);
        }
    }
    if (!slot) {
        throw new DefineSlotError(`googletag.defineSlot did not return a slot`, sizeMapping);
    }
    const slotReady = initSlotIas(id, slot);
    void slotReady.then(() => {
        EventTimer.get().mark('defineSlotEnd', slotTarget);
        EventTimer.get().mark('slotReady', slotTarget);
        // wait until IAS has initialised before checking teads eligibility
        const isTeadsEligible = isEligibleForTeads(id);
        if (isTeadsEligible) {
            slot.setTargeting('teadsEligible', 'true');
        }
        else {
            slot.setTargeting('teadsEligible', 'false');
        }
    });
    const isbn = window.guardian.config.page.isbn;
    if (slotTarget === isbn) {
        slot.setTargeting('isbn', isbn);
    }
    const fabricKeyValues = new Map([
        ['top-above-nav', 'fabric1'],
        ['merchandising-high', 'fabric2'],
        ['merchandising', 'fabric3'],
    ]);
    const slotFabric = fabricKeyValues.get(slotTarget);
    if (slotFabric) {
        slot.setTargeting('slot-fabric', slotFabric);
    }
    Object.entries(slotTargeting).forEach(([key, value]) => {
        slot.setTargeting(key, value);
    });
    slot.addService(window.googletag.pubads())
        .setTargeting('slot', slotTarget)
        .setTargeting('testgroup', String(Math.floor(100 * Math.random())));
    return {
        slot,
        slotReady,
    };
};
export { buildGoogletagSizeMapping, collectSizes, defineSlot, canDefineSlot, DefineSlotError, };
